import { useEffect, useState } from "react";
import { adminLogin, fetchInquiries } from "../lib/api";
import { toast } from "sonner";
import { Lock, RefreshCw, LogOut, Mail, Phone, Building2 } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem("pew_admin_token") || "");
  const [password, setPassword] = useState("");
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (t) => {
    setLoading(true);
    try {
      const data = await fetchInquiries(t);
      setInquiries(data);
    } catch {
      toast.error("Session expired. Please sign in again.");
      localStorage.removeItem("pew_admin_token");
      setToken("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    try {
      const r = await adminLogin(password);
      localStorage.setItem("pew_admin_token", r.token);
      setToken(r.token);
      toast.success("Welcome back.");
    } catch {
      toast.error("Invalid password");
    }
  };

  const logout = () => {
    localStorage.removeItem("pew_admin_token");
    setToken("");
    setInquiries([]);
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--maroon-950)] grain text-white p-6">
        <form
          data-testid="admin-login-form"
          onSubmit={onLogin}
          className="w-full max-w-md bg-[#FDFBF7] text-[var(--text-primary)] p-10 border border-[var(--border-subtle)]"
        >
          <Link to="/" className="overline" data-testid="admin-back-home">← Back to site</Link>
          <div className="flex items-center gap-3 mt-4">
            <Lock size={22} className="text-[var(--maroon-700)]" />
            <h1 className="font-display text-3xl text-[var(--maroon-950)]">Admin sign-in</h1>
          </div>
          <p className="mt-3 text-[var(--text-secondary)] text-sm">
            Enter the admin password to view inquiries.
          </p>
          <div className="mt-8">
            <label className="field-label" htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              className="field-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          <button data-testid="admin-login-submit" type="submit" className="btn-primary mt-8 w-full">
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-[var(--bg-primary)]">
      <header className="border-b border-[var(--border-subtle)] bg-[#FDFBF7]">
        <div className="container-edge py-5 flex items-center justify-between">
          <div>
            <div className="overline">PEW Admin</div>
            <h1 className="font-display text-2xl md:text-3xl text-[var(--maroon-950)] mt-1">Inquiries</h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => load(token)} className="btn-secondary">
              <RefreshCw size={16} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={logout} className="btn-primary">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="container-edge py-12">
        {loading ? (
          <div className="text-[var(--text-secondary)]">Loading inquiries...</div>
        ) : inquiries.length === 0 ? (
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] p-12 text-center">
            <h2 className="font-display text-3xl text-[var(--maroon-950)]">No inquiries yet</h2>
            <p className="mt-3 text-[var(--text-secondary)]">As visitors submit the quote form, leads will appear here.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {inquiries.map((q) => (
              <article
                key={q.id}
                data-testid={`inquiry-card-${q.id}`}
                className="bg-white border border-[var(--border-subtle)] p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-display text-xl text-[var(--maroon-950)]">{q.name}</h3>
                    {q.company && (
                      <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)] mt-1">
                        <Building2 size={14} /> {q.company}
                      </div>
                    )}
                  </div>
                  <div className="font-mono-accent text-[11px] tracking-[0.2em] uppercase text-[var(--maroon-700)]">
                    {new Date(q.created_at).toLocaleString()}
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <a href={`mailto:${q.email}`} className="flex items-center gap-2 text-[var(--maroon-900)] hover:underline">
                    <Mail size={14} /> {q.email}
                  </a>
                  <a href={`tel:${q.phone}`} className="flex items-center gap-2 text-[var(--maroon-900)] hover:underline">
                    <Phone size={14} /> {q.phone}
                  </a>
                </div>

                {q.product && (
                  <div className="mt-4 inline-block bg-[var(--bg-secondary)] px-3 py-1 font-mono-accent text-[11px] tracking-[0.18em] uppercase text-[var(--maroon-700)]">
                    {q.product}
                  </div>
                )}
                <p className="mt-4 text-[var(--text-secondary)] leading-relaxed text-[15px] whitespace-pre-line">
                  {q.message}
                </p>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
